import { useState, useEffect, useRef } from 'react';
import api from '../api';

const PLACEHOLDERS = [
  ['{{audit_name}}',            'Naam van de audit'],
  ['{{client_name}}',           'Naam van de klant'],
  ['{{framework_name}}',        'Framework (bijv. ISO 27001:2022)'],
  ['{{auditor_name}}',          'Naam van de auditor'],
  ['{{audit_date}}',            'Auditdatum'],
  ['{{generated_date}}',        'Datum van rapportgeneratie'],
  ['{{status}}',                'Status van de audit'],
  ['{{scope}}',                 'Toepassingsgebied'],
  ['{{audit_description}}',     'Omschrijving auditronde'],
  ['{{auditees}}',              'Auditees (als ze overal gelijk zijn)'],
  ['{{total}}',                 'Totaal aantal controls'],
  ['{{conform_count}}',         'Aantal conform'],
  ['{{niet_conform_count}}',    'Aantal niet conform'],
  ['{{observatie_count}}',      'Aantal observaties'],
  ['{{kans_verbetering_count}}','Aantal kansen voor verbetering'],
  ['{{nvt_count}}',             'Aantal N.v.t.'],
  ['{{niet_beoordeeld_count}}', 'Aantal niet beoordeeld'],
];

const FINDING_PLACEHOLDERS = [
  ['{{control_id}}',    'Control-nummer (bijv. A.5.1)'],
  ['{{title}}',         'Titel van de control'],
  ['{{section}}',       'Sectie (bijv. A.5)'],
  ['{{status_label}}',  'Status in tekst (bijv. Conform)'],
  ['{{situation}}',     'Huidige situatie'],
  ['{{finding}}',       'Bevinding'],
  ['{{recommendation}}','Aanbeveling'],
  ['{{priority}}',      'Prioriteit'],
  ['{{due_date}}',      'Streefdatum'],
  ['{{auditees}}',      'Auditees (per control, als ze verschillen)'],
  ['{{maturity}}',      'Maturiteit (ISO 27001 Annex A)'],
  ['{{notes}}',         'Notities'],
];

export default function TemplatesPage() {
  const [templates,   setTemplates]   = useState([]);
  const [frameworks,  setFrameworks]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showUpload,  setShowUpload]  = useState(false);
  const [uploading,   setUploading]   = useState(false);
  const [error,       setError]       = useState('');
  const [form,        setForm]        = useState({ name: '', framework_id: '' });
  const fileRef = useRef(null);

  function load() {
    Promise.all([api.get('/api/templates'), api.get('/api/frameworks')])
      .then(([t, f]) => { setTemplates(t.data); setFrameworks(f.data); })
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function handleUpload(e) {
    e.preventDefault();
    const file = fileRef.current?.files[0];
    if (!file) { setError('Selecteer een .docx bestand'); return; }
    if (!form.name) { setError('Geef een naam op'); return; }

    setUploading(true); setError('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', form.name);
    if (form.framework_id) fd.append('framework_id', form.framework_id);

    try {
      await api.post('/api/templates', fd);
      setShowUpload(false);
      setForm({ name: '', framework_id: '' });
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Fout bij uploaden');
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(id, name) {
    if (!window.confirm(`Template "${name}" verwijderen?`)) return;
    await api.delete(`/api/templates/${id}`);
    load();
  }

  function handleDownload(id) {
    window.open(`/api/templates/${id}/download`, '_blank');
  }

  // Groepeer per framework
  const grouped = frameworks.map(fw => ({
    fw,
    items: templates.filter(t => String(t.framework_id) === String(fw.id)),
  })).concat([{
    fw: { id: null, name: 'Standaard (geen framework-koppeling)' },
    items: templates.filter(t => !t.framework_id),
  }]).filter(g => g.items.length > 0 || !g.fw.id);

  return (
    <>
      <div className="page-header">
        <h2>Rapport templates</h2>
        <button className="btn btn-primary btn-sm" onClick={() => { setShowUpload(true); setError(''); }}>
          <i className="bi bi-upload me-1" />Template uploaden
        </button>
      </div>

      <div className="page-body">
        {/* Upload modal */}
        {showUpload && (
          <>
            <div className="modal show d-block" tabIndex="-1">
              <div className="modal-dialog modal-lg modal-dialog-scrollable">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Template uploaden</h5>
                    <button className="btn-close" onClick={() => setShowUpload(false)} />
                  </div>
                  <div className="modal-body">
                    {error && <div className="alert alert-danger py-2">{error}</div>}
                    <form id="upload-form" onSubmit={handleUpload}>
                      <div className="mb-3">
                        <label className="form-label small fw-medium">Naam *</label>
                        <input className="form-control" value={form.name}
                          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                          required placeholder="bijv. ISO 27001 rapport FDConsult" autoFocus />
                      </div>
                      <div className="mb-3">
                        <label className="form-label small fw-medium">Framework</label>
                        <select className="form-select" value={form.framework_id}
                          onChange={e => setForm(f => ({ ...f, framework_id: e.target.value }))}>
                          <option value="">— Standaard (alle frameworks) —</option>
                          {frameworks.map(fw => (
                            <option key={fw.id} value={fw.id}>{fw.name}{fw.version ? ` (${fw.version})` : ''}</option>
                          ))}
                        </select>
                        <div className="form-text">
                          Koppel de template aan een specifiek framework. Bij het genereren wordt automatisch de juiste template gekozen.
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label small fw-medium">Word-bestand (.docx) *</label>
                        <input type="file" className="form-control" accept=".docx" ref={fileRef} required />
                      </div>
                    </form>

                    {/* Placeholder referentie */}
                    <hr />
                    <div className="small">
                      <div className="fw-semibold mb-2">📋 Beschikbare placeholders in de template</div>
                      <div className="row g-3">
                        <div className="col-12 col-md-6">
                          <div className="text-muted mb-1 fw-medium" style={{ fontSize: 11 }}>AUDIT-GEGEVENS</div>
                          <table className="table table-sm table-bordered mb-0" style={{ fontSize: 12 }}>
                            <tbody>
                              {PLACEHOLDERS.map(([ph, desc]) => (
                                <tr key={ph}>
                                  <td className="font-monospace text-primary py-1 px-2" style={{ whiteSpace: 'nowrap' }}>{ph}</td>
                                  <td className="text-muted py-1 px-2">{desc}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="col-12 col-md-6">
                          <div className="text-muted mb-1 fw-medium" style={{ fontSize: 11 }}>PER BEVINDING (in loop)</div>
                          <div className="alert alert-info py-2 small mb-2">
                            Zet <code>{'{{#findings}}'}</code> en <code>{'{{/findings}}'}</code> rond een tabelrij om die te herhalen per bevinding.
                            Voor per-sectie groepering gebruik <code>{'{{#sections}}'}</code> + <code>{'{{#section_findings}}'}</code>.
                          </div>
                          <table className="table table-sm table-bordered mb-0" style={{ fontSize: 12 }}>
                            <tbody>
                              {FINDING_PLACEHOLDERS.map(([ph, desc]) => (
                                <tr key={ph}>
                                  <td className="font-monospace text-primary py-1 px-2" style={{ whiteSpace: 'nowrap' }}>{ph}</td>
                                  <td className="text-muted py-1 px-2">{desc}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowUpload(false)}>Annuleren</button>
                    <button type="submit" form="upload-form" className="btn btn-primary" disabled={uploading}>
                      {uploading
                        ? <><span className="spinner-border spinner-border-sm me-2" />Uploaden…</>
                        : <><i className="bi bi-upload me-1" />Uploaden</>}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop show" />
          </>
        )}

        {/* Intro */}
        <div className="alert alert-info d-flex gap-2 mb-4 py-2">
          <i className="bi bi-info-circle mt-1" />
          <div className="small">
            Upload een <strong>.docx bestand</strong> met placeholders zoals <code>{'{{audit_name}}'}</code> en <code>{'{{#findings}}...{{/findings}}'}</code>.
            Het systeem vult automatisch de juiste template in bij rapport-generatie op basis van het framework.
            Geen template? Dan wordt de standaard lay-out gebruikt.
          </div>
        </div>

        {loading ? (
          <div className="text-muted"><span className="spinner-border spinner-border-sm me-2" />Laden…</div>
        ) : (
          <>
            {frameworks.map(fw => {
              const items = templates.filter(t => String(t.framework_id) === String(fw.id));
              if (items.length === 0) return null;
              return (
                <div key={fw.id} className="mb-4">
                  <h6 className="fw-semibold text-muted mb-2">{fw.name}{fw.version ? ` (${fw.version})` : ''}</h6>
                  <TemplateList items={items} onDelete={handleDelete} onDownload={handleDownload} />
                </div>
              );
            })}

            {/* Templates zonder framework-koppeling */}
            {templates.filter(t => !t.framework_id).length > 0 && (
              <div className="mb-4">
                <h6 className="fw-semibold text-muted mb-2">Standaard (alle frameworks)</h6>
                <TemplateList
                  items={templates.filter(t => !t.framework_id)}
                  onDelete={handleDelete}
                  onDownload={handleDownload}
                />
              </div>
            )}

            {templates.length === 0 && (
              <div className="card">
                <div className="card-body text-center text-muted py-4">
                  <i className="bi bi-file-earmark-word d-block mb-2" style={{ fontSize: 32 }} />
                  Nog geen templates. Upload een .docx bestand om te starten.
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

function TemplateList({ items, onDelete, onDownload }) {
  return (
    <div className="card">
      <div className="table-responsive">
        <table className="table table-hover mb-0">
          <thead className="table-light">
            <tr>
              <th>Naam</th>
              <th>Origineel bestand</th>
              <th>Geüpload op</th>
              <th>Door</th>
              <th style={{ width: 120 }} />
            </tr>
          </thead>
          <tbody>
            {items.map(t => (
              <tr key={t.id}>
                <td className="fw-medium">{t.name}</td>
                <td className="text-muted small">{t.original_filename}</td>
                <td className="text-muted small">{new Date(t.created_at).toLocaleDateString('nl-BE')}</td>
                <td className="text-muted small">{t.created_by_name}</td>
                <td>
                  <div className="d-flex gap-1 justify-content-end">
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => onDownload(t.id)}
                      title="Downloaden">
                      <i className="bi bi-download" />
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => onDelete(t.id, t.name)}
                      title="Verwijderen">
                      <i className="bi bi-trash" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
